import { Suspense } from "react"
import { Lens } from "@hookform/lenses"
import { ExternalAssistanceContent } from "@/modules/patients/agenda/external-assistance-information-dialog"
import type { BetterDocCheck } from "@/modules/patients/detail/better-doc/hooks/useBetterDocRejectionConfirmation"
import {
  isActualEmergency,
  isActualGeneralPractitioner,
  isActualHospitalElective,
  isActualHospitalEmergency,
  isActualSpecialist,
  isActualTelecare,
  isExternalAssistance,
  isMedicalEmergency,
  isMedicalGeneralPractitioner,
  isMedicalHospitalElective,
  isMedicalHospitalEmergency,
  isMedicalSpecialist,
  isTelecareContent,
} from "@/modules/patients/detail/progress-note/point-of-care/components/point-of-care-content-conditions"
import { EmergencyContent } from "@/modules/patients/detail/progress-note/point-of-care/components/point-of-care-types/emergency-content"
import { GeneralPractitionerContent } from "@/modules/patients/detail/progress-note/point-of-care/components/point-of-care-types/general-practitioner-content"
import { HospitalElectiveContent } from "@/modules/patients/detail/progress-note/point-of-care/components/point-of-care-types/hospital-elective-content"
import { HospitalEmergencyContent } from "@/modules/patients/detail/progress-note/point-of-care/components/point-of-care-types/hospital-emergency-content"
import { SpecialistContent } from "@/modules/patients/detail/progress-note/point-of-care/components/point-of-care-types/specialist-content"
import { TelecareContent } from "@/modules/patients/detail/progress-note/point-of-care/components/point-of-care-types/telecare-content"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import type {
  PointOfCareRecord,
  TimeToTreatByPointOfCare,
} from "@/modules/patients/detail/progress-note/point-of-care/types"
import type { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { MedgateServiceInfo } from "@/modules/patients/services/usePatientByIdQuery"
import type { Country, PointOfCareType } from "@/types/__generated__/union-types"

interface PointOfCareContentProps {
  pointOfCareFormLens: Lens<PointOfCareFormType>
  patientId: string
  medgateServiceInfo: MedgateServiceInfo
  countryCode: Country
  medicalPointOfCare: PointOfCareRecord
  actualPointOfCare: PointOfCareRecord
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  diagnosisCode: string
  hasCareProvider: boolean
  timeToTreatByPointOfCare: TimeToTreatByPointOfCare
  setCareProvider: (careProvider: string | null) => void
  onBetterDocCheck: BetterDocCheck
  onMedicalPointOfCareSelect: (value: PointOfCareType) => void
  onActualPointOfCareSelect: (value: PointOfCareType | undefined) => void
  onSpecialtySelect: (specialtyId: string | undefined) => void
}

export const PointOfCareContent = (props: PointOfCareContentProps): React.JSX.Element | null => {
  const {
    pointOfCareFormLens,
    patientId,
    medgateServiceInfo,
    countryCode,
    medicalPointOfCare: medical,
    actualPointOfCare: actual,
    episodeOfCareProgressNoteData,
    diagnosisCode,
    hasCareProvider,
    timeToTreatByPointOfCare,
    setCareProvider,
    onBetterDocCheck,
    onMedicalPointOfCareSelect,
    onActualPointOfCareSelect,
    onSpecialtySelect,
  } = props

  if (isMedicalGeneralPractitioner(medical, actual) || isActualGeneralPractitioner(medical, actual)) {
    return (
      <GeneralPractitionerContent
        pointOfCareFormLens={pointOfCareFormLens}
        medgateServiceInfo={medgateServiceInfo}
        isActual={isActualGeneralPractitioner(medical, actual)}
        countryCode={countryCode}
        timeToTreatData={timeToTreatByPointOfCare.GENERAL_PRACTITIONER}
        hasCareProvider={hasCareProvider}
        hasMPNReferral={medgateServiceInfo.referralToGeneralPractitioner === "MPN"}
        patientId={patientId}
        episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
        diagnosisCode={diagnosisCode}
        setCareProvider={setCareProvider}
        onBetterDocCheck={onBetterDocCheck}
        onMedicalPointOfCareSelect={onMedicalPointOfCareSelect}
        onActualPointOfCareSelect={onActualPointOfCareSelect}
      />
    )
  }

  if (isMedicalSpecialist(medical, actual) || isActualSpecialist(medical, actual)) {
    return (
      <SpecialistContent
        pointOfCareFormLens={pointOfCareFormLens}
        medgateServiceInfo={medgateServiceInfo}
        patientId={patientId}
        isActual={isActualSpecialist(medical, actual)}
        countryCode={countryCode}
        timeToTreatData={timeToTreatByPointOfCare.SPECIALIST}
        episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
        diagnosisCode={diagnosisCode}
        hasCareProvider={hasCareProvider}
        setCareProvider={setCareProvider}
        onBetterDocCheck={onBetterDocCheck}
        onSpecialtySelect={onSpecialtySelect}
      />
    )
  }

  if (isMedicalHospitalElective(medical, actual) || isActualHospitalElective(medical, actual)) {
    return (
      <HospitalElectiveContent
        pointOfCareFormLens={pointOfCareFormLens}
        isActual={isActualHospitalElective(medical, actual)}
        timeToTreatData={timeToTreatByPointOfCare.HOSPITAL_ELECTIVE}
        countryCode={countryCode}
        hasCareProvider={hasCareProvider}
        episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
        diagnosisCode={diagnosisCode}
        setCareProvider={setCareProvider}
        onBetterDocCheck={onBetterDocCheck}
      />
    )
  }

  if (isMedicalEmergency(medical, actual) || isActualEmergency(medical, actual)) {
    return (
      <EmergencyContent
        pointOfCareFormLens={pointOfCareFormLens}
        isActual={isActualEmergency(medical, actual)}
        timeToTreatData={timeToTreatByPointOfCare.MEDICAL_EMERGENCY_OR_PERMANENCE}
        countryCode={countryCode}
        hasCareProvider={hasCareProvider}
      />
    )
  }

  if (isMedicalHospitalEmergency(medical, actual) || isActualHospitalEmergency(medical, actual)) {
    return (
      <HospitalEmergencyContent
        pointOfCareFormLens={pointOfCareFormLens}
        isActual={isActualHospitalEmergency(medical, actual)}
        timeToTreatData={timeToTreatByPointOfCare.HOSPITAL_EMERGENCY_ROOM}
        countryCode={countryCode}
        hasCareProvider={hasCareProvider}
      />
    )
  }

  if (isTelecareContent(medical, actual)) {
    return (
      <TelecareContent
        pointOfCareFormLens={pointOfCareFormLens}
        isActual={isActualTelecare(medical, actual)}
        medicalPointOfCare={medical}
        actualPointOfCare={actual}
      />
    )
  }

  if (isExternalAssistance(medical)) {
    return (
      <Suspense>
        <div className="col-span-2">
          <ExternalAssistanceContent patientId={patientId} className="bg-surface-subdued ml-0" />
        </div>
      </Suspense>
    )
  }

  return null
}
